import clsx from 'clsx'
import * as React from 'react'
import { Logger } from 'src/3rds/cube'
import { classToFComponent, FCClassContext } from 'src/3rds/cube-react'

import { makeStyles } from 'tss-react/mui'

import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import SendIcon from '@mui/icons-material/Send'

import {
    comoVaiSair,
    contador,
    detalheDaSituacao,
    formatarTelefone,
    podeEnviar,
    quando,
    SITUACOES
} from '../MensagemDireta.regras'
import { LIMITE_TEXTO, MensagemDiretaScope, Parceiro, Recado, RECADOS_POR_PAGINA } from '../MensagemDireta.scopes'

const LOG = Logger.get('MensagemDireta-VIEW')

const numero = (n: number) => n.toLocaleString('pt-BR')

const useStyles = makeStyles()((theme) => ({
    view: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: theme.spacing(3),
        rowGap: theme.spacing(2.5),
        // O body do portal é fixo em 100vh, então o módulo precisa rolar por conta própria.
        overflow: 'auto',
        minHeight: 0,
        // Sem isto os cards encolhem e recortam o conteúdo (o Card do MUI tem overflow: hidden).
        '& > *': {
            flexShrink: 0
        }
    },
    cabecalho: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap'
    },
    acoesCabecalho: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center'
    },
    card: {
        borderRadius: 14,
        boxShadow: '0px 2px 12px rgba(0,0,0,0.05)'
    },
    tituloCard: {
        fontWeight: 600,
        fontSize: 15
    },
    filtros: {
        display: 'flex',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        alignItems: 'center',
        marginTop: theme.spacing(1.5)
    },
    texto: {
        maxWidth: 460,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        // Recado longo: mostra o começo; o texto inteiro fica na dica.
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden'
    },
    secundario: {
        fontSize: 12,
        color: theme.palette.text.secondary
    },
    telefone: {
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap'
    },
    conversa: {
        display: 'flex',
        // Invertida, a rolagem começa embaixo: o que aparece primeiro são as mensagens mais recentes.
        flexDirection: 'column-reverse',
        gap: theme.spacing(0.75),
        maxHeight: 280,
        overflow: 'auto',
        padding: theme.spacing(1.5),
        borderRadius: 10,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100]
    },
    balao: {
        maxWidth: '80%',
        padding: theme.spacing(0.75, 1.25),
        borderRadius: 10,
        fontSize: 13,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        backgroundColor: theme.palette.background.paper
    },
    balaoParceiro: {
        alignSelf: 'flex-start'
    },
    balaoLia: {
        alignSelf: 'flex-end',
        backgroundColor: theme.palette.mode === 'dark' ? '#1f3d2b' : '#dcf8c6'
    },
    horaBalao: {
        display: 'block',
        fontSize: 11,
        color: theme.palette.text.secondary,
        marginTop: 2
    }
}))

export type MensagemDiretaViewProps = {
    className?: string
    scope: MensagemDiretaScope
}

class MensagemDiretaClass implements FCClassContext<MensagemDiretaViewProps> {
    scope!: MensagemDiretaScope
    styles!: ReturnType<typeof useStyles>

    // :: emissors

    readonly emitAtualizar = () => this.scope.onAtualizar().catch(LOG.caught)
    readonly emitBusca = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onBuscaChange(evt.target.value).catch(LOG.caught)
    readonly emitPagina = (_evt: unknown, pagina: number) => this.scope.onPaginaChange(pagina).catch(LOG.caught)

    readonly emitAbrirNova =
        (ownerId = '') =>
        () =>
            this.scope.onAbrirNova(ownerId).catch(LOG.caught)
    readonly emitFecharNova = () => this.scope.onFecharNova().catch(LOG.caught)
    readonly emitBuscaParceiro = (_evt: unknown, texto: string, motivo: string) => {
        if (motivo === 'input') this.scope.onBuscaParceiroChange(texto).catch(LOG.caught)
    }
    readonly emitParceiro = (_evt: unknown, parceiro: Parceiro | null) =>
        this.scope.onParceiroChange(parceiro).catch(LOG.caught)
    readonly emitTexto = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onTextoChange(evt.target.value).catch(LOG.caught)
    readonly emitConversa = () => this.scope.onConversaToggle().catch(LOG.caught)
    readonly emitEnviar = () => this.scope.onEnviar().catch(LOG.caught)

    readonly emitAbrirCancelar = (id: string) => () => this.scope.onAbrirCancelar(id).catch(LOG.caught)
    readonly emitFecharCancelar = () => this.scope.onFecharCancelar().catch(LOG.caught)
    readonly emitConfirmarCancelar = () => this.scope.onConfirmarCancelar().catch(LOG.caught)

    // :: Methods

    onSyncState({ scope }: MensagemDiretaViewProps) {
        this.scope = scope
        this.styles = useStyles()
    }

    render({ className }: MensagemDiretaViewProps) {
        const { classes } = this.styles

        return (
            <div className={clsx(classes.view, className)}>
                <Box className={classes.cabecalho}>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Mensagem direta
                        </Typography>
                        <Typography color="text.secondary" fontSize={15}>
                            Comunique a um parceiro algo específico para ele, pela LIA.
                        </Typography>
                    </Box>
                    <Box className={classes.acoesCabecalho}>
                        <Button size="small" startIcon={<RefreshIcon />} onClick={this.emitAtualizar}>
                            Atualizar
                        </Button>
                        <Button variant="contained" startIcon={<SendIcon />} onClick={this.emitAbrirNova()}>
                            Nova mensagem
                        </Button>
                    </Box>
                </Box>

                <Alert severity="info">
                    O recado sai pelo número da LIA, sem o nome de quem escreveu. Se o parceiro escreveu à LIA nas
                    últimas 24 horas, ele vai na hora. Senão, fica pendente e a LIA manda um aviso com o botão “Ver
                    mensagem”: o recado sai quando o parceiro tocar no botão ou escrever.
                </Alert>

                {this.renderRecados()}
                {this.renderDialogoNova()}
                {this.renderDialogoCancelar()}
            </div>
        )
    }

    renderRecados() {
        const { scope } = this
        const { classes } = this.styles

        return (
            <Card className={classes.card}>
                {scope.carregandoLista && <LinearProgress />}
                <CardContent>
                    <Typography className={classes.tituloCard}>Recados enviados</Typography>
                    <Box className={classes.filtros}>
                        <TextField
                            size="small"
                            sx={{ flex: '1 1 260px', maxWidth: 420 }}
                            placeholder="Buscar por parceiro, telefone ou texto"
                            value={scope.busca}
                            onChange={this.emitBusca}
                            inputProps={{ 'aria-label': 'Buscar recado' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                    <TableContainer sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Para</TableCell>
                                    <TableCell>Recado</TableCell>
                                    <TableCell>Escrito</TableCell>
                                    <TableCell>Situação</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scope.recados.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            {scope.busca.trim() ? 'Nenhum recado encontrado.' : 'Nenhum recado ainda.'}
                                        </TableCell>
                                    </TableRow>
                                )}
                                {scope.recados.map((r) => this.renderLinha(r))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={scope.total}
                        page={scope.pagina}
                        onPageChange={this.emitPagina}
                        rowsPerPage={RECADOS_POR_PAGINA}
                        rowsPerPageOptions={[RECADOS_POR_PAGINA]}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${numero(count)}`}
                    />
                </CardContent>
            </Card>
        )
    }

    renderLinha(r: Recado) {
        const { classes } = this.styles
        const situacao = SITUACOES[r.situacao] ?? SITUACOES.enviado

        return (
            <TableRow key={r.id} sx={r.situacao === 'cancelado' ? { opacity: 0.65 } : undefined}>
                <TableCell>
                    {r.parceiro}
                    <div className={clsx(classes.secundario, classes.telefone)}>{formatarTelefone(r.telefone)}</div>
                </TableCell>
                <TableCell>
                    <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{r.texto}</span>} describeChild>
                        <div className={classes.texto}>{r.texto}</div>
                    </Tooltip>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {quando(r.criadoEm)}
                    <div className={classes.secundario}>por {r.operador}</div>
                </TableCell>
                <TableCell>
                    <Tooltip title={detalheDaSituacao(r)} describeChild>
                        <Chip size="small" color={situacao.cor} variant="outlined" label={situacao.rotulo} />
                    </Tooltip>
                    {r.enviadoEm && <div className={classes.secundario}>saiu {quando(r.enviadoEm)}</div>}
                    {r.situacao === 'pendente' && r.avisoEm && (
                        <div className={classes.secundario}>aviso {quando(r.avisoEm)}</div>
                    )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {r.situacao === 'pendente' && (
                        <Button size="small" color="error" onClick={this.emitAbrirCancelar(r.id)}>
                            Cancelar
                        </Button>
                    )}
                    <Button size="small" onClick={this.emitAbrirNova(r.ownerId)}>
                        Nova para ele
                    </Button>
                </TableCell>
            </TableRow>
        )
    }

    renderDialogoNova() {
        const { scope } = this
        const parceiro = scope.parceiro
        const saida = parceiro ? comoVaiSair(parceiro) : undefined
        const longo = scope.texto.length > LIMITE_TEXTO

        return (
            <Dialog open={scope.novaAberta} onClose={this.emitFecharNova} fullWidth maxWidth="md">
                <DialogTitle>Nova mensagem</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        sx={{ mt: 1 }}
                        options={scope.opcoesParceiro}
                        value={parceiro ?? null}
                        onChange={this.emitParceiro}
                        onInputChange={this.emitBuscaParceiro}
                        // A lista já vem filtrada pelo servidor (são milhares de parceiros).
                        filterOptions={(opcoes) => opcoes}
                        loading={scope.buscandoParceiros}
                        loadingText="Buscando parceiros…"
                        noOptionsText={
                            scope.buscaParceiro.trim().length < 2
                                ? 'Digite o nome, o e-mail ou o telefone'
                                : 'Nenhum parceiro encontrado'
                        }
                        getOptionLabel={(p) => `${p.nome} — ${p.email}`}
                        isOptionEqualToValue={(a, b) => a.ownerId === b.ownerId}
                        renderOption={(props, p) => (
                            <li {...props} key={p.ownerId}>
                                <Box>
                                    {p.nome}
                                    <Typography variant="caption" display="block" color="text.secondary">
                                        {p.email} · {formatarTelefone(p.telefone)}
                                        {p.janelaAberta ? ' · escreveu nas últimas 24 h' : ''}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Parceiro" autoFocus={!parceiro} />}
                    />

                    {parceiro && saida && (
                        <Alert severity={saida.severity} sx={{ mt: 2 }}>
                            Para {formatarTelefone(parceiro.telefone)}. {saida.texto}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={14}
                        sx={{ mt: 2 }}
                        label="Recado"
                        placeholder="Escreva o que a LIA vai mandar ao parceiro."
                        value={scope.texto}
                        onChange={this.emitTexto}
                        autoFocus={!!parceiro}
                        error={longo || !!scope.erroEnvio}
                        helperText={
                            scope.erroEnvio ??
                            (longo
                                ? `Passa do limite do WhatsApp (${contador(scope.texto)}).`
                                : `Sai como texto, sem o seu nome. ${contador(scope.texto)}`)
                        }
                    />

                    {parceiro && this.renderConversa()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.emitFecharNova} disabled={scope.enviando}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={this.emitEnviar}
                        disabled={!podeEnviar(parceiro, scope.texto) || scope.enviando}
                    >
                        {scope.enviando ? 'Enviando…' : 'Enviar'}
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    renderConversa() {
        const { scope } = this
        const { classes } = this.styles

        return (
            <Box sx={{ mt: 2 }}>
                <Button
                    size="small"
                    onClick={this.emitConversa}
                    endIcon={scope.conversaAberta ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    aria-expanded={scope.conversaAberta}
                >
                    Conversa do parceiro com a LIA
                </Button>
                <Collapse in={scope.conversaAberta} unmountOnExit>
                    {scope.carregandoConversa && <LinearProgress sx={{ mt: 1 }} />}
                    <Box className={classes.conversa} sx={{ mt: 1 }}>
                        {!scope.carregandoConversa && scope.conversa.length === 0 && (
                            <Typography className={classes.secundario}>Sem mensagens com a LIA.</Typography>
                        )}
                        {[...scope.conversa].reverse().map((m) => (
                            <div
                                key={m.id}
                                className={clsx(classes.balao, m.sent ? classes.balaoParceiro : classes.balaoLia)}
                            >
                                {m.content}
                                <span className={classes.horaBalao}>
                                    {m.sent ? 'Parceiro' : 'LIA'} · {quando(m.timestamp)}
                                </span>
                            </div>
                        ))}
                    </Box>
                </Collapse>
            </Box>
        )
    }

    renderDialogoCancelar() {
        const { scope } = this
        const recado = scope.cancelarAberto

        return (
            <Dialog open={!!recado} onClose={this.emitFecharCancelar} fullWidth maxWidth="sm">
                <DialogTitle>Cancelar o recado para {recado?.parceiro}?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        O recado não sai mais, mesmo que o parceiro toque em “Ver mensagem”. O aviso, se já foi, não
                        volta atrás: tocando no botão, o parceiro recebe só os outros recados pendentes ou a resposta de
                        que não há mensagem nova.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.emitFecharCancelar} disabled={scope.cancelando}>
                        Voltar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={this.emitConfirmarCancelar}
                        disabled={scope.cancelando}
                    >
                        Cancelar o recado
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }
}

export const MensagemDiretaView = classToFComponent(MensagemDiretaClass, React)
export default MensagemDiretaView
